/**
 * API Service — Admin Panel
 * Il Diario di Bordo - SaaS
 *
 * MIGRAZIONE httpOnly Cookie:
 * - I token JWT sono gestiti via cookie httpOnly (impostati dal backend)
 * - Il frontend NON legge/scrive più token in localStorage
 * - CSRF token inviato come header X-CSRF-Token
 * - withCredentials: true per inviare cookie cross-origin
 */

import axios from 'axios'
import { getCsrfToken } from '../utils/csrf'

const API_URL = import.meta.env.PROD
  ? 'https://api.ildiariodibordo.it/api'
  : (import.meta.env.VITE_API_URL || '/api')

// Callback per force-logout (impostato da AuthContext)
let _onForceLogout = null
export const setOnForceLogout = (callback) => { _onForceLogout = callback }

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  // Cookie httpOnly inviati automaticamente dal browser
  withCredentials: true
})

// Request interceptor - aggiunge CSRF token
api.interceptors.request.use(
  (config) => {
    // Aggiungi CSRF token per richieste mutanti
    if (['post', 'put', 'delete', 'patch'].includes(config.method)) {
      const csrfToken = getCsrfToken()
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken
      }
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor - handle token refresh
let isRefreshing = false

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Non ritentare se siamo su pagine pubbliche
    const currentPath = window.location.pathname
    if (currentPath === '/login' || currentPath === '/2fa-verify') {
      return Promise.reject(error)
    }

    // If 401 and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry && !isRefreshing) {
      originalRequest._retry = true
      isRefreshing = true

      try {
        // Il refresh token è nel cookie httpOnly — il browser lo invia automaticamente
        await axios.post(
          `${API_URL}/auth/refresh`,
          {}, // Body vuoto
          { withCredentials: true }
        )

        // Il backend ha impostato nuovi cookie — riprova la richiesta originale
        return api(originalRequest)
      } catch (refreshError) {
        // Refresh failed — notifica AuthContext per reset state (no page reload)
        if (_onForceLogout) _onForceLogout()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

export default api

// ==========================================
// 2FA LOGIN API (usa token temporaneo, non JWT)
// Endpoint: /api/auth/2fa-login/*
// Nota: withCredentials: true per ricevere i cookie nella risposta
// ==========================================
export const twoFactorLoginApi = {
  // Verifica codice 2FA (TOTP, Email OTP, Backup Code)
  verify: (twoFactorToken, code, method = 'totp', rememberDevice = false) => {
    return axios.post(`${API_URL}/auth/2fa-login/verify`, {
      twoFactorToken,
      code,
      method,
      rememberDevice
    }, { withCredentials: true })
  },

  // Verifica con WebAuthn/Passkey
  verifyWebAuthn: (twoFactorToken, webauthnResponse, rememberDevice = false) => {
    return axios.post(`${API_URL}/auth/2fa-login/verify`, {
      twoFactorToken,
      method: 'webauthn',
      webauthnResponse,
      rememberDevice
    }, { withCredentials: true })
  },

  // Invia codice OTP via email
  sendEmailCode: (twoFactorToken) => {
    return axios.post(`${API_URL}/auth/2fa-login/send-code`, {
      twoFactorToken
    }, { withCredentials: true })
  },

  // Ottieni opzioni WebAuthn per autenticazione
  getWebAuthnOptions: (twoFactorToken) => {
    return axios.post(`${API_URL}/auth/2fa-login/webauthn/auth-options`, {
      twoFactorToken
    }, { withCredentials: true })
  },

  // Controlla se dispositivo è fidato (per skip 2FA)
  checkTrustedDevice: (twoFactorToken) => {
    return axios.post(`${API_URL}/auth/2fa-login/check-trusted`, {
      twoFactorToken
    }, { withCredentials: true })
  }
}

// ==========================================
// ADMIN API HELPERS
// ==========================================
export const adminApi = {
  // Dashboard
  getDashboard: () => api.get('/admin/dashboard'),
  getSystemStatus: () => api.get('/admin/system-status'),
  getDashboardStats: () => api.get('/admin/stats/dashboard'),

  // Tenants
  getTenants: (params) => api.get('/admin/tenants', { params }),
  getTenant: (id) => api.get(`/admin/tenants/${id}`),
  getTenantUsers: (id, params) => api.get(`/admin/tenants/${id}/users`, { params }),
  extendTrial: (id, days) => api.post(`/admin/tenants/${id}/extend-trial`, { days }),
  changePlan: (id, plan, durationDays) => api.post(`/admin/tenants/${id}/change-plan`, { plan, durationDays }),
  suspendTenant: (id) => api.post(`/admin/tenants/${id}/suspend`),
  reactivateTenant: (id) => api.post(`/admin/tenants/${id}/reactivate`),
  deleteTenant: (id) => api.delete(`/admin/tenants/${id}`, { data: { confirm: 'DELETE' } }),

  // Coupons
  getCoupons: (params) => api.get('/admin/coupons', { params }),
  createCoupon: (data) => api.post('/admin/coupons', data),
  updateCoupon: (id, data) => api.put(`/admin/coupons/${id}`, data),
  deleteCoupon: (id) => api.delete(`/admin/coupons/${id}`),
  syncCouponToStripe: (id) => api.post(`/admin/sync-stripe-coupon/${id}`),

  // Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
  updateTrialSettings: (data) => api.put('/admin/settings/trial', data),
  updateCompanySettings: (data) => api.put('/admin/settings/company', data),
  updateLegalSettings: (data) => api.put('/admin/settings/legal', data),
  updateFeatures: (data) => api.put('/admin/settings/features', data),

  // Admins
  getAdmins: () => api.get('/admin/admins'),
  createAdmin: (data) => api.post('/admin/admins', data),

  // Plans
  getPlans: () => api.get('/admin/plans'),
  getPlan: (planId) => api.get(`/admin/plans/${planId}`),
  updatePlan: (planId, data) => api.put(`/admin/plans/${planId}`, data),
  initializePlans: () => api.post('/admin/plans/initialize'),
  getPlansComparison: () => api.get('/admin/plans/comparison'),
  togglePlan: (planId) => api.post(`/admin/plans/${planId}/toggle`),

  // Access Logs
  getLogs: (params) => api.get('/admin/logs', { params }),
  getFailedLogins: (params) => api.get('/admin/logs/failed-logins', { params }),
  getTenantLogs: (tenantId) => api.get(`/admin/logs/tenant/${tenantId}`),
  getLogsStats: () => api.get('/admin/logs/stats'),
  getImpersonationLogs: (params) => api.get('/admin/logs/impersonations', { params }),

  // Security Dashboard
  getSecurityOverview: () => api.get('/admin/logs/security/overview'),
  getSecurityAlerts: (params) => api.get('/admin/logs/security/alerts', { params }),
  acknowledgeSecurityAlert: (id, payload) => api.post(`/admin/logs/security/alerts/${id}/acknowledge`, payload),
  runAnomalyDetection: () => api.post('/admin/logs/security/run-now'),

  // Announcements
  getAnnouncements: (params) => api.get('/announcements/admin', { params }),
  getAnnouncement: (id) => api.get(`/announcements/admin/${id}`),
  createAnnouncement: (data) => api.post('/announcements/admin', data),
  updateAnnouncement: (id, data) => api.put(`/announcements/admin/${id}`, data),
  deleteAnnouncement: (id) => api.delete(`/announcements/admin/${id}`),
  toggleAnnouncement: (id) => api.post(`/announcements/admin/${id}/toggle`),
  createMaintenanceNotice: (data) => api.post('/announcements/admin/maintenance', data),

  // Impersonation
  impersonateUser: (userId) => api.post(`/admin/impersonate/${userId}`),
  endImpersonation: () => api.post('/admin/impersonate/end'),

  // GDPR Export
  exportTenantData: (tenantId) => api.get(`/admin/export/tenant/${tenantId}`),
  exportUserData: (userId) => api.get(`/admin/export/user/${userId}`),

  // Export auditato con identity verification
  startExportVerification: (tenantId) =>
    api.get(`/admin/tenants/${tenantId}/export/verify/start`),
  // submit ritorna JSON (esito verifica + header X-Export-Session-Token)
  submitExportVerification: (tenantId, answers, exportFilters) =>
    api.post(`/admin/tenants/${tenantId}/export/verify/submit`,
      { answers, exportFilters },
      { timeout: 30000 }),
  // generate può ritornare blob (download) o JSON (email/async)
  generateExportWithToken: (tenantId, sessionToken, exportFilters) =>
    api.post(`/admin/tenants/${tenantId}/export/generate`,
      { sessionToken, exportFilters },
      { responseType: 'blob', timeout: 120000 }),

  // 2FA Management
  get2FAStats: () => api.get('/admin/2fa/stats'),
  get2FAUsers: (params) => api.get('/admin/2fa/users', { params }),
  get2FAUser: (userId) => api.get(`/admin/2fa/users/${userId}`),
  get2FAAudit: (params) => api.get('/admin/2fa/audit', { params }),
  reset2FA: (userId, data) => api.post(`/admin/2fa/users/${userId}/reset`, data),
  forceReset2FA: (userId, data) => api.post(`/admin/2fa/users/${userId}/force-reset`, data),

  // Identity Verification
  startIdentityVerification: (userId) => api.get(`/admin/2fa/identity-verification/start/${userId}`),
  submitIdentityVerification: (data) => api.post('/admin/2fa/identity-verification/submit', data),
  getIdentityVerificationHistory: (userId) => api.get(`/admin/2fa/identity-verification/history/${userId}`),
  getIdentityVerificationReport: (reportId) => api.get(`/admin/2fa/identity-verification/report/${reportId}`),
  applyIdentityVerificationOverride: (reportId, data) => api.post(`/admin/2fa/identity-verification/${reportId}/override`, data),

  // Stripe Webhook IPs
  getStripeIPStatus: () => api.get('/admin/stripe-ips'),
  checkStripeIPs: () => api.post('/admin/stripe-ips/check'),
  syncStripeIPs: () => api.post('/admin/stripe-ips/sync'),
  updateStripeIPs: (ips) => api.put('/admin/stripe-ips', { ips }),
  addStripeIP: (ip) => api.post('/admin/stripe-ips/add', { ip }),
  removeStripeIP: (ip) => api.delete(`/admin/stripe-ips/${ip}`),

  // Organizations - FASE 8
  getOrganizations: (params) => api.get('/admin/organizations', { params }),
  getOrganization: (id) => api.get(`/admin/organizations/${id}`),
  getOrganizationStats: (id) => api.get(`/admin/organizations/${id}/stats`),
  updateOrganization: (id, data) => api.put(`/admin/organizations/${id}`, data),
  suspendOrganization: (id, reason) => api.post(`/admin/organizations/${id}/suspend`, { reason }),
  reactivateOrganization: (id) => api.post(`/admin/organizations/${id}/reactivate`),
  getOrganizationMigrations: (id) => api.get(`/admin/organizations/${id}/migrations`),
  approveOrganizationMigration: (orgId, centerId) =>
    api.post(`/admin/organizations/${orgId}/migrations/${centerId}/approve`),
  rejectOrganizationMigration: (orgId, centerId, reason) =>
    api.post(`/admin/organizations/${orgId}/migrations/${centerId}/reject`, { reason }),
  extendOrganizationTrials: (id, days) =>
    api.post(`/admin/organizations/${id}/extend-trials`, { days }),
  getOrganizationsOverview: () => api.get('/admin/organizations/stats/overview'),

  // Experimental Features
  toggleExperimentalFeature: (tenantId, feature, enabled) =>
    api.put(`/admin/tenants/${tenantId}/experimental-features`, { feature, enabled }),
  getExperimentalStats: () => api.get('/admin/experimental-features/stats'),

  // Legal Documents - FASE 3
  getLegalDocuments: (params = {}) => {
    const query = new URLSearchParams(params).toString()
    return api.get(`/legal/admin/documents${query ? `?${query}` : ''}`)
  },
  getLegalDocument: (id) => api.get(`/legal/admin/documents/${id}`),
  createLegalDocument: (data) => api.post('/legal/admin/documents', data),
  updateLegalDocument: (id, data) => api.put(`/legal/admin/documents/${id}`, data),
  deleteLegalDocument: (id) => api.delete(`/legal/admin/documents/${id}`),
  publishLegalDocument: (id, options = {}) => api.post(`/legal/admin/documents/${id}/publish`, options),
  archiveLegalDocument: (id) => api.post(`/legal/admin/documents/${id}/archive`),
  uploadLegalDocumentPdf: (id, formData) => {
    return api.post(`/legal/admin/documents/${id}/upload-pdf`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  deleteLegalDocumentPdf: (id) => api.delete(`/legal/admin/documents/${id}/pdf`),
  getLegalDocumentSignatures: (id, params = {}) => {
    const query = new URLSearchParams(params).toString()
    return api.get(`/legal/admin/documents/${id}/signatures${query ? `?${query}` : ''}`)
  },
  sendLegalDocumentReminder: (id) => api.post(`/legal/admin/documents/${id}/send-reminder`),
  requestLegalDocumentResignature: (id, options = {}) => api.post(`/legal/admin/documents/${id}/request-resignature`, options),

  // Compliance — vista trasversale per organization
  getNonCompliantOrganizations: (params = {}) => {
    const query = new URLSearchParams(params).toString()
    return api.get(`/legal/admin/non-compliant-organizations${query ? `?${query}` : ''}`)
  },
  getOrganizationCompliance: (orgId) => api.get(`/legal/admin/organizations/${orgId}/compliance`),
  extendComplianceDeadline: (orgId, payload) => api.post(`/legal/admin/organizations/${orgId}/extend-deadline`, payload),
  grantComplianceExemption: (orgId, payload) => api.post(`/legal/admin/organizations/${orgId}/grant-exemption`, payload),
  revokeComplianceException: (orgId, exceptionId) => api.delete(`/legal/admin/organizations/${orgId}/exceptions/${exceptionId}`)
}
